import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Collapse,
  Tooltip,
  LinearProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      color: theme.palette.primary.contrastText,
      fontWeight: 'bold',
      fontSize: '0.9rem',
    }
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    transform: 'scale(1.01)',
    transition: 'all 0.2s ease-in-out',
  },
  cursor: 'pointer'
}));

const ScoreCell = styled(TableCell)(({ theme, score }) => {
  let color = theme.palette.success.main;
  if (score < 60) color = theme.palette.error.main;
  else if (score < 70) color = theme.palette.warning.main;
  
  return {
    fontWeight: 'bold',
    color: color,
    fontSize: '1.1rem'
  };
});

const ExpandIcon = styled(IconButton)(({ expanded }) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease-in-out',
}));

const getGrade = (score) => {
  if (score >= 80) return { grade: 'A', color: 'success' };
  if (score >= 70) return { grade: 'B', color: 'info' };
  if (score >= 60) return { grade: 'C', color: 'warning' };
  if (score >= 50) return { grade: 'D', color: 'error' };
  return { grade: 'F', color: 'error' };
};

const SkillRadarChart = ({ data, color }) => {
  const radarData = [
    { skill: 'การปฏิบัติงาน', value: data.performance, fullMark: 5 },
    { skill: 'ทักษะเฉพาะ', value: data.skills, fullMark: 5 },
    { skill: 'การสื่อสาร', value: data.communication, fullMark: 5 },
    { skill: 'ภาวะผู้นำ', value: data.leadership, fullMark: 5 },
    { skill: 'พฤติกรรม', value: data.behavior, fullMark: 5 },
    { skill: 'เจตคติ', value: data.attitude, fullMark: 5 }
  ];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RadarChart data={radarData}>
        <PolarGrid />
        <PolarAngleAxis dataKey="skill" tick={{ fontSize: 12 }} />
        <PolarRadiusAxis 
          angle={90} 
          domain={[0, 5]} 
          tick={{ fontSize: 10 }}
          tickCount={6}
        />
        <Radar
          name="คะแนน"
          dataKey="value"
          stroke={color}
          fill={color}
          fillOpacity={0.3}
          strokeWidth={2}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
};

const ProgramComparisonTable = ({ programs = [] }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleRowClick = (programCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(programCode)) {
      newExpanded.delete(programCode);
    } else {
      newExpanded.add(programCode);
    }
    setExpandedRows(newExpanded);
  };

  const sortedPrograms = [...programs].sort((a, b) => b.avg_score - a.avg_score);

  return (
    <Box>
      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>หลักสูตร</TableCell>
              <TableCell align="center">จำนวนนิสิต</TableCell>
              <TableCell align="center">คะแนนเฉลี่ย</TableCell>
              <TableCell align="center">เกรด</TableCell>
              <TableCell align="center">ส่วนเบียงเบน</TableCell>
              <TableCell align="center">Top 10%</TableCell>
              <TableCell align="center">รายละเอียด</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPrograms.map((program, index) => {
              const { grade, color } = getGrade(program.avg_score);
              const isExpanded = expandedRows.has(program.program_code);
              
              return (
                <React.Fragment key={program.program_code}>
                  <StyledTableRow onClick={() => handleRowClick(program.program_code)}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: program.color, 
                            width: 40, 
                            height: 40,
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {program.program_code}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {program.program_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {program.program_name}
                          </Typography>
                        </Box>
                        {index === 0 && (
                          <Tooltip title="อันดับ 1" arrow>
                            <EmojiEventsIcon sx={{ color: 'gold', ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <GroupIcon fontSize="small" color="action" />
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {program.student_count}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <ScoreCell align="center" score={program.avg_score}>
                      {program.avg_score.toFixed(1)}
                    </ScoreCell>
                    
                    <TableCell align="center">
                      <Chip 
                        label={grade}
                        color={color}
                        size="small"
                        sx={{ fontWeight: 'bold', minWidth: 40 }}
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2">
                        ±{program.std_deviation.toFixed(1)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <TrendingUpIcon fontSize="small" color="success" />
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {program.top_10_percent}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <ExpandIcon 
                        expanded={isExpanded}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(program.program_code);
                        }}
                        size="small"
                      >
                        <ExpandMoreIcon />
                      </ExpandIcon>
                    </TableCell>
                  </StyledTableRow>
                  
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Grid container spacing={3}>
                            {/* Score Range */}
                            <Grid item xs={12} md={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom color="primary">
                                    📊 ช่วงคะแนน
                                  </Typography>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      คะแนนสูงสุด
                                    </Typography>
                                    <Typography variant="h6" color="success.main">
                                      {program.max_score} คะแนน
                                    </Typography>
                                  </Box>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      คะแนนต่ำสุด
                                    </Typography>
                                    <Typography variant="h6" color="error.main">
                                      {program.min_score} คะแนน
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      ช่วงคะแนน
                                    </Typography>
                                    <Typography variant="h6">
                                      {program.max_score - program.min_score} คะแนน
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                            
                            {/* Skills Breakdown */}
                            <Grid item xs={12} md={8}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom color="primary">
                                    🎯 การประเมินด้านทักษะ
                                  </Typography>
                                  <SkillRadarChart 
                                    data={program.skill_breakdown} 
                                    color={program.color}
                                  />
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          💡 <strong>หมายเหตุ:</strong> คลิกที่แถวเพื่อดูรายละเอียดการประเมินทักษะของแต่ละหลักสูตร | 
          ข้อมูลเรียงตามคะแนนเฉลี่ยจากสูงไปต่ำ
        </Typography>
      </Box>
    </Box>
  );
};

export default ProgramComparisonTable;
